
import React, { useState, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Upload, 
  FileText, 
  X, 
  Loader2, 
  Download 
} from "lucide-react";
import { uploadBrandGuidelines, getBrandGuidelinesUrl } from "@/utils/fileUploader";
import { useToast } from "@/hooks/use-toast";
import { ERROR_CODES } from "@/lib/errorCodes";

interface GuidelinesFileUploaderProps {
  businessId?: string;
  fileName?: string;
  filePath?: string;
  existingFiles?: string[]; 
  onFileUploaded: (fileName: string, filePath: string) => void;
}

const GuidelinesFileUploader: React.FC<GuidelinesFileUploaderProps> = ({ 
  businessId = "", 
  fileName = "", 
  filePath = "", 
  existingFiles = [], 
  onFileUploaded 
}) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !businessId) return;

    setIsUploading(true);
    setUploadError(null);
    
    try {
      const result = await uploadBrandGuidelines(file, businessId);
      
      if (result.success && result.filePath) {
        // Update with file info
        onFileUploaded(file.name, result.filePath);
        
        toast({
          title: "Upload successful",
          description: "Your brand guidelines have been uploaded successfully.",
        });
      } else {
        setUploadError(result.error?.description || ERROR_CODES.FILE_UPLOAD_FAILED.description);
        toast({
          title: result.error?.message || ERROR_CODES.FILE_UPLOAD_FAILED.message,
          description: result.error?.description || ERROR_CODES.FILE_UPLOAD_FAILED.description,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadError(ERROR_CODES.FILE_UPLOAD_FAILED.description);
      toast({
        title: ERROR_CODES.FILE_UPLOAD_FAILED.message,
        description: ERROR_CODES.FILE_UPLOAD_FAILED.description,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the file input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = () => {
    onFileUploaded("", "");
    toast({
      title: "File removed",
      description: "Your brand guidelines file has been removed.",
    });
  };

  const handleDownloadFile = () => {
    if (!filePath) return;
    
    const url = getBrandGuidelinesUrl(filePath);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-2">
      <Label>Brand Guidelines Document</Label>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        onChange={handleFileChange}
        className="hidden"
      />
      
      {filePath ? (
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <span className="font-medium">{fileName}</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownloadFile}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRemoveFile}
                className="text-destructive hover:text-destructive/80"
                title="Remove"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div 
          className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:bg-muted/10 transition-colors cursor-pointer"
          onClick={handleFileClick}
        >
          {isUploading ? (
            <Loader2 className="h-10 w-10 text-primary mx-auto mb-2 animate-spin" />
          ) : (
            <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          )}
          <p className="text-sm font-medium">
            {isUploading ? "Uploading..." : "Upload Brand Guidelines"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, DOCX (5MB max)
          </p>
        </div>
      )}
      
      {uploadError && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default GuidelinesFileUploader;
